import type { Category, Product, Status, Visibility } from './types';

/**
 * One saturated hue per product so the board can be scanned without reading.
 * Kept in sync with the CSS custom properties in index.css and with
 * mobile/src/theme.ts — the three surfaces share one visual language.
 *
 * GPU NeevCloud and RackBank are temporarily disabled (see the Product type in
 * lib/types.ts). Re-enable by adding their entries back here — the theme
 * values below are kept for when that happens:
 *   gpu_neevcloud: { label: 'GPU NeevCloud', short: 'GPU', hex: '#34d399',
 *     chip: 'border-[#34d399]/35 bg-[#34d399]/12 text-[#7ee7bf]', dot: 'bg-[#34d399]',
 *     glow: 'rgb(52 211 153 / 0.35)' }
 *   rackbank: { label: 'RackBank', short: 'RACK', hex: '#f5a524',
 *     chip: 'border-[#f5a524]/35 bg-[#f5a524]/12 text-[#f9c877]', dot: 'bg-[#f5a524]',
 *     glow: 'rgb(245 165 36 / 0.35)' }
 */
export const PRODUCT_THEME: Record<
  Product,
  { label: string; short: string; hex: string; chip: string; dot: string; glow: string }
> = {
  cpu_neevcloud: {
    label: 'CPU NeevCloud',
    short: 'CPU',
    hex: '#4c8dff',
    chip: 'border-[#4c8dff]/35 bg-[#4c8dff]/12 text-product-cpu',
    dot: 'bg-[#4c8dff]',
    glow: 'rgb(76 141 255 / 0.35)',
  },
  pulse_neevcloud: {
    label: 'Pulse NeevCloud',
    short: 'PULSE',
    hex: '#f472b6',
    chip: 'border-[#f472b6]/35 bg-[#f472b6]/12 text-product-pulse',
    dot: 'bg-[#f472b6]',
    glow: 'rgb(244 114 182 / 0.35)',
  },
};

export const PRODUCTS = Object.keys(PRODUCT_THEME) as Product[];

export const CATEGORY_META: Record<Category, { label: string; icon: string }> = {
  ui_bug: { label: 'UI Bug', icon: '◈' },
  customer_feedback: { label: 'Customer Feedback', icon: '❝' },
  feature_request: { label: 'Feature Request', icon: '✦' },
  performance_issue: { label: 'Performance', icon: '⚡' },
  other: { label: 'Other', icon: '•' },
};

export const CATEGORIES = Object.keys(CATEGORY_META) as Category[];

export const STATUS_META: Record<Status, { label: string; chip: string; dot: string }> = {
  new: {
    label: 'New',
    chip: 'border-status-new/35 bg-status-new/12 text-status-new',
    dot: 'bg-status-new',
  },
  in_progress: {
    label: 'In Progress',
    chip: 'border-status-progress/35 bg-status-progress/12 text-status-progress',
    dot: 'bg-status-progress',
  },
  resolved: {
    label: 'Resolved',
    chip: 'border-status-resolved/35 bg-status-resolved/12 text-status-resolved',
    dot: 'bg-status-resolved',
  },
};

export const STATUSES = Object.keys(STATUS_META) as Status[];

/** Mirrors the server's STATUS_TRANSITIONS so the UI only offers legal moves. */
export const STATUS_TRANSITIONS: Record<Status, Status[]> = {
  new: ['in_progress', 'resolved'],
  in_progress: ['resolved', 'new'],
  resolved: ['in_progress'],
};

export const VISIBILITY_META: Record<Visibility, { label: string; hint: string }> = {
  public: { label: 'Public', hint: 'Everyone signed in can see this' },
  private: { label: 'Private', hint: 'Only admins and the assignee can see this' },
};

export const PRIORITY_LABELS: Record<number, string> = {
  1: 'Minimal',
  2: 'Low',
  3: 'Moderate',
  4: 'High',
  5: 'Critical',
};
